import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { loginMutation } from "@/lib/client/@tanstack/react-query.gen";
import { setAuth } from "@/stores/auth.store";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/login" as "/")({
	component: AdminLoginPage,
});

const loginSchema = z.object({
	email: z.string().email("Email invalide"),
	password: z
		.string()
		.min(6, "Le mot de passe doit contenir au moins 6 caractères"),
});

function AdminLoginPage() {
	const navigate = useNavigate();

	const {
		mutate: login,
		isPending,
		error,
	} = useMutation({
		...loginMutation(),
		onSuccess: async (data: any) => {
			if (data?.token && data?.role === "ADMIN") {
				setAuth(data, data.token);
				navigate({ to: "/admin" });
			} else {
				throw new Error("Accès non autorisé");
			}
		},
	});

	const form = useForm({
		defaultValues: {
			email: "",
			password: "",
		},
		onSubmit: async ({ value }) => {
			const isValid = loginSchema.safeParse(value);
			if (isValid.success) {
				login({ body: value });
			}
		},
	});

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-bold text-center">
						Administration
					</CardTitle>
					<CardDescription className="text-center">
						Connexion à l'espace administrateur Revacours
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						<div className="space-y-2">
							<form.Field
								name="email"
								children={(field) => (
									<>
										<Label htmlFor={field.name}>Email</Label>
										<Input
											id={field.name}
											type="email"
											placeholder="admin@revacours.com"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
										/>
									</>
								)}
							/>
						</div>

						<div className="space-y-2">
							<form.Field
								name="password"
								children={(field) => (
									<>
										<Label htmlFor={field.name}>Mot de passe</Label>
										<Input
											id={field.name}
											type="password"
											placeholder="••••••••"
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
										/>
									</>
								)}
							/>
						</div>

						{error && (
							<Alert variant="destructive">
								<AlertDescription>
									Email ou mot de passe incorrect
								</AlertDescription>
							</Alert>
						)}

						<Button type="submit" className="w-full" disabled={isPending}>
							{isPending ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Connexion...
								</>
							) : (
								"Se connecter"
							)}
						</Button>
					</form>
				</CardContent>
				<CardFooter>
					<div className="text-sm text-center w-full">
						<Link
							to="/forgot-password"
							className="text-blue-600 hover:underline hover:text-blue-700 transition-colors"
						>
							Mot de passe oublié ?
						</Link>
					</div>
				</CardFooter>
			</Card>
		</div>
	);
}
